import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDateToShortDotV2, Toast, useT } from '@repo/common';

import { ReactComponent as OptionIcon } from '@repo/common/assets/icons/20px/icon_20px_option_filled.svg';
import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/20px/icon_20px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/20px/icon_20px_arrow-top.svg';
import OptionList from '../AdminPayments/PaymentsHistory/OptionList';
import RejectRefundModal from './RejectRefundModal';
import ApproveRefundModal from './ApproveRefundModal';
import { ADMIN_CARD_CLASS } from '../AdminPayments/paymentColumns';
import { REFUND_COLUMN_CLASS as COL } from './refundColumns';
import {
  refundPayment,
  rejectRefund,
} from '../../../api/payment/payment.api';

// 카드 결제는 기존 표기("15.00 $"), KRW 계좌이체는 "55,000 KRW"
const formatMoney = (value, currency) =>
  String(currency || '').toUpperCase() === 'KRW'
    ? `${Math.round(Number(value || 0)).toLocaleString('en-US')} KRW`
    : `${Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} $`;

const STATUS_STYLE = {
  REQUESTED: {
    label: 'Refund requested',
    labelKr: '환불 요청',
    cls: 'bg-purple-purple01 text-text-accent',
  },
  PROCESSING: {
    label: 'Refund processing',
    labelKr: '환불 처리 중',
    cls: 'bg-purple-purple01 text-text-accent',
  },
  COMPLETED: {
    label: 'Refunded',
    labelKr: '환불 완료',
    cls: 'bg-green-light02 text-green-deep04',
  },
  REJECTED: {
    label: 'Refund rejected',
    labelKr: '환불 거절',
    cls: 'bg-[rgba(255,239,239,0.9)] text-functional-failed03',
  },
  CANCELED: {
    label: 'Canceled',
    labelKr: '취소됨',
    cls: 'bg-grayscale-gray02 text-text-secondary',
  },
};

const resolveStatus = (item) => {
  if (item.kind !== 'REFUND') return STATUS_STYLE.CANCELED;
  return STATUS_STYLE[item.refundStatus] ?? STATUS_STYLE.COMPLETED;
};

const getPaymentStatus = (statuses = []) => {
  if (!statuses.length) return 'UNPAID';
  const paidSet = ['CONFIRMED', 'ATTENDED', 'ABSENT'];
  if (statuses.every((s) => s === 'CANCELED')) return 'CANCELED';
  const paidCount = statuses.filter((s) => paidSet.includes(s)).length;
  if (paidCount === statuses.length) return 'FULLY PAID';
  if (paidCount > 0) return 'PARTIALLY PAID';
  return 'UNPAID';
};

/**
 * Refunds & Cancellations 한 행 (취소 번호 단위).
 * 환불 요청(REQUESTED/PROCESSING) 행에는 승인·거절 액션이 붙는다.
 *  - 승인: 카드는 PG 취소, 계좌이체는 주최측 송금 확인 뒤 "환불 완료" 기록 → 게스트에게 완료 메일
 *  - 거절: 예약을 Confirmed 로 복구 + 좌석 재점유 → 게스트에게 미승인 메일
 */
const AdminRefundsRow = ({ item, onFetch }) => {
  const t = useT();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [showOptionList, setShowOptionList] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);

  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [toast, setToast] = useState('');

  const isBankTransfer =
    String(item.paymentProvider || '').toUpperCase() === 'BANK_TRANSFER';
  const status = resolveStatus(item);
  const isActionable =
    item.kind === 'REFUND' &&
    item.paymentId != null &&
    ['REQUESTED', 'PROCESSING'].includes(item.refundStatus);

  const totalAmount = Array.isArray(item.amounts)
    ? item.amounts.reduce((sum, v) => sum + Number(v || 0), 0)
    : Number(item.amount || 0);
  const formattedAmount = formatMoney(totalAmount, item.currency);
  const paymentStatus = getPaymentStatus(item.statuses);
  const cancellationDate = formatDateToShortDotV2(item.cancellationDate);
  const processedDate = item.refundProcessedAt
    ? formatDateToShortDotV2(item.refundProcessedAt)
    : '';

  const bookingItems = (item.bookingIds || []).map((id, index) => ({
    id,
    title: item.workshopTitles?.[index],
    workshopId: item.workshopIds?.[index],
    amount: item.amounts?.[index],
    serial: item.bookingSerialNumbers?.[index],
  }));

  const showToast = (message) => {
    setToast('');
    setTimeout(() => setToast(message), 10);
    setTimeout(() => setToast(''), 4000);
  };

  const errorMessage = (err, fallback) =>
    err?.response?.data?.msg || err?.response?.data?.message || fallback;

  const handleViewInvoice = () => {
    setShowOptionList(false);
    if (!item.invoiceNumber) return;
    navigate(`/mypage?tab=Payments&invoice=${item.invoiceNumber}`);
  };

  const handleDownloadReceipt = () => {
    if (item.receiptUrl) window.open(item.receiptUrl, '_blank');
    setShowOptionList(false);
  };

  // amount: 어드민이 확인한 환불 금액. 전액이면 refundAmount 를 비워 보내 서버가 "전액 환불"로 처리한다
  const handleApprove = async (amount, isFull) => {
    if (processing) return;
    setProcessing(true);
    try {
      await refundPayment({
        paymentId: item.paymentId,
        refundAmount: isFull ? undefined : amount,
        refundReason: item.cancelReason || 'Refund approved by admin',
        adminOverrideReason: isBankTransfer
          ? 'Organizer refunded by bank transfer'
          : undefined,
      });
      setShowApproveModal(false);
      showToast(t('Refund completed.', '환불이 완료 처리되었습니다.'));
      onFetch?.();
    } catch (err) {
      console.error('Failed to approve refund:', err);
      alert(
        errorMessage(
          err,
          t('Could not complete the refund.', '환불 처리에 실패했습니다.'),
        ),
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (reason) => {
    if (processing) return;
    setProcessing(true);
    try {
      await rejectRefund({ paymentId: item.paymentId, reason });
      setShowRejectModal(false);
      showToast(
        t(
          'Refund rejected. Booking restored to Confirmed.',
          '환불을 거절하고 예약을 복구했습니다.',
        ),
      );
      onFetch?.();
    } catch (err) {
      console.error('Failed to reject refund:', err);
      alert(
        errorMessage(
          err,
          t('Could not reject the refund.', '환불 거절에 실패했습니다.'),
        ),
      );
    } finally {
      setProcessing(false);
    }
  };

  const optionButton = (
    <button
      type='button'
      aria-label='Refund options'
      className='text-icon-placeholder hover:text-icon-tertiary active:text-icon-tertiary'
      onClick={(e) => {
        e.stopPropagation();
        setAnchorRect(e.currentTarget.getBoundingClientRect());
        setSelectedStatus(paymentStatus);
        setShowOptionList(true);
      }}
    >
      <OptionIcon />
    </button>
  );

  const toggleIcon = isOpen ? <ArrowTopIcon /> : <ArrowBottomIcon />;

  const statusBadge = (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-md px-1.5 py-[2px] typo-b11Strong ${status.cls}`}
    >
      {t(status.label, status.labelKr)}
    </span>
  );

  const actionLinks = isActionable && (
    <div className='flex items-center gap-2 typo-b11Strong'>
      <button
        type='button'
        className='text-green-deep03 underline hover:opacity-80'
        onClick={(e) => {
          e.stopPropagation();
          setShowApproveModal(true);
        }}
      >
        {isBankTransfer ? t('Mark refunded', '환불 완료') : t('Approve', '승인')}
      </button>
      <span className='text-text-placeholder'>·</span>
      <button
        type='button'
        className='text-functional-failed03 underline hover:opacity-80'
        onClick={(e) => {
          e.stopPropagation();
          setShowRejectModal(true);
        }}
      >
        {t('Reject', '거절')}
      </button>
    </div>
  );

  const providerTag = isBankTransfer ? (
    <span className='typo-b11 text-text-placeholder whitespace-nowrap'>
      {t('Bank transfer', '계좌이체')}
    </span>
  ) : null;

  return (
    <div className='w-full'>
      {/* 표 — tabletV(1180px) 이상 */}
      <div
        className={`hidden tabletV:flex flex-col w-full items-start ${
          isOpen ? 'py-1 bg-surface-lightgray60' : ''
        }`}
      >
        <div
          className={`flex w-full min-h-[48px] items-center ${
            !isOpen ? 'bg-grayscale-gray00' : ''
          }`}
        >
          <div className={`flex justify-center items-center p-1 ${COL.date}`}>
            <span className='typo-b13 text-text-disable block w-full text-center truncate'>
              {cancellationDate}
            </span>
          </div>
          <div className={`flex justify-center items-center p-1 ${COL.number}`}>
            <span className='typo-b14 text-text-secondary truncate'>
              {item.canceledSerialNumbers}
            </span>
          </div>
          <div
            className={`flex flex-col justify-center items-center p-1 text-center min-w-0 ${COL.reason}`}
          >
            <span className='typo-b14Strong text-text-primary truncate block w-full'>
              {item.cancelReason || '-'}
            </span>
            {item.refundStatus === 'REJECTED' && item.adminNote && (
              <span className='typo-b11 text-text-placeholder truncate block w-full'>
                {t('Admin note:', '어드민 메모:')} {item.adminNote}
              </span>
            )}
          </div>
          <div className={`flex justify-center items-center p-1 ${COL.user}`}>
            <span className='typo-b14 text-text-secondary truncate block w-full text-center'>
              {item.userName}
            </span>
          </div>
          <div
            className={`flex flex-col justify-center items-center p-1 ${COL.amount}`}
          >
            <span className='typo-b14 text-text-secondary whitespace-nowrap'>
              {formattedAmount}
            </span>
            {providerTag}
          </div>
          <div
            className={`flex flex-col justify-center items-center gap-1 p-1 ${COL.status}`}
          >
            {statusBadge}
            {isActionable ? (
              actionLinks
            ) : (
              <span className='typo-b11 text-text-placeholder'>
                {processedDate || cancellationDate}
              </span>
            )}
          </div>
          <div className={`flex justify-center items-center ${COL.icon}`}>
            {optionButton}
          </div>
          <div className={`flex justify-start items-center ${COL.icon}`}>
            <button
              type='button'
              aria-label={isOpen ? 'Collapse' : 'Expand'}
              className='text-icon-placeholder'
              onClick={() => setIsOpen((prev) => !prev)}
            >
              {toggleIcon}
            </button>
          </div>
        </div>

        {isOpen && (
          <div className='w-full flex flex-col items-start p-4'>
            <div className='w-full border border-grayscale-gray02'>
              {bookingItems.map((booking, index) => (
                <div
                  key={booking.id}
                  className={`flex h-[38px] items-center typo-b13 text-text-secondary w-full ${
                    index < bookingItems.length - 1
                      ? 'border-b border-grayscale-gray02'
                      : ''
                  }`}
                >
                  <span className='flex items-center justify-center p-1 w-[130px] gap-1 border-r border-grayscale-gray02 h-full'>
                    <span className='text-text-placeholder'>
                      {t('Item ID', '항목 ID')}
                    </span>
                    <span>#{String(index + 1).padStart(2, '0')}</span>
                  </span>
                  <div className='flex flex-1 min-w-0 items-center justify-center p-1 gap-2.5 border-r border-grayscale-gray02 w-full h-full'>
                    <span
                      className='truncate block w-full text-text-primary hover:underline cursor-pointer text-center'
                      onClick={() =>
                        navigate(
                          `/community/kpathport/workshop/${booking.workshopId}`,
                        )
                      }
                    >
                      {t('[Workshop]', '[워크숍]')} {booking.title}
                      {booking.serial ? ` · ${booking.serial}` : ''}
                    </span>
                  </div>
                  <span className='flex items-center justify-center p-1 w-[120px] gap-2.5 truncate min-w-0 h-full'>
                    {formatMoney(booking.amount, item.currency)}
                  </span>
                </div>
              ))}
            </div>
            <div className='flex justify-between items-center gap-3 w-full mt-4'>
              <div className='typo-b12 text-text-secondary'>
                {item.userEmail}
                {item.invoiceNumber
                  ? ` · ${t('Invoice', '인보이스')} ${item.invoiceNumber}`
                  : ''}
              </div>
              <div className='flex items-center gap-4'>
                {isActionable && actionLinks}
                <button
                  type='button'
                  className='typo-b12 text-text-accent underline'
                  onClick={handleViewInvoice}
                >
                  {t('View Invoice detail', '인보이스 상세 보기')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 카드 — tabletV 미만 */}
      <div className={ADMIN_CARD_CLASS}>
        <div className='flex items-start justify-between gap-2'>
          <div className='flex flex-col min-w-0 gap-1'>
            <div className='flex items-center gap-2 flex-wrap'>
              {statusBadge}
              {providerTag}
            </div>
            <span className='typo-b14Strong text-text-primary line-clamp-2'>
              {item.cancelReason || '-'}
            </span>
            <span className='typo-b12 text-text-secondary truncate'>
              {item.canceledSerialNumbers} · {item.userName}
            </span>
          </div>
          <div className='flex-shrink-0 -mr-1'>{optionButton}</div>
        </div>
        <div className='flex flex-wrap items-center gap-x-3 gap-y-1 typo-b13 text-text-secondary'>
          <span className='typo-b13Strong text-text-primary'>
            {formattedAmount}
          </span>
          <span className='text-text-placeholder'>
            {processedDate || cancellationDate}
          </span>
        </div>
        <div className='flex justify-between items-center'>
          <div>{isActionable && actionLinks}</div>
          <button
            type='button'
            className='flex items-center gap-1 typo-b13 text-text-secondary'
            onClick={() => setIsOpen((prev) => !prev)}
          >
            {bookingItems.length}{' '}
            {bookingItems.length === 1 ? 'item' : 'items'}
            {toggleIcon}
          </button>
        </div>
        {isOpen && (
          <div className='flex flex-col gap-2 pt-2 border-t border-grayscale-gray02'>
            {bookingItems.map((booking, index) => (
              <div
                key={booking.id}
                className='flex items-center gap-2 typo-b13 text-text-secondary'
              >
                <span className='flex-shrink-0 text-text-placeholder'>
                  #{String(index + 1).padStart(2, '0')}
                </span>
                <span
                  className='flex-1 min-w-0 truncate typo-b13Strong text-text-primary'
                  onClick={() =>
                    navigate(
                      `/community/kpathport/workshop/${booking.workshopId}`,
                    )
                  }
                >
                  {t('[Workshop]', '[워크숍]')} {booking.title}
                </span>
                <span className='flex-shrink-0'>
                  {formatMoney(booking.amount, item.currency)}
                </span>
              </div>
            ))}
            <button
              type='button'
              className='self-end typo-b12 text-text-accent underline'
              onClick={handleViewInvoice}
            >
              {t('View Invoice detail', '인보이스 상세 보기')}
            </button>
          </div>
        )}
      </div>

      {showOptionList && (
        <OptionList
          anchorRect={anchorRect}
          onClose={() => setShowOptionList(false)}
          status={selectedStatus}
          originalStatus={item.statuses}
          hasReceipt={Boolean(item.receiptUrl)}
          onDownloadReceipt={handleDownloadReceipt}
          onViewInvoice={handleViewInvoice}
        />
      )}

      <ApproveRefundModal
        isOpen={showApproveModal}
        onClose={() => !processing && setShowApproveModal(false)}
        onConfirm={handleApprove}
        loading={processing}
        total={totalAmount}
        currency={item.currency}
        isBankTransfer={isBankTransfer}
        userName={item.userName}
        requestReason={item.cancelReason}
      />

      <RejectRefundModal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        onConfirm={handleReject}
        loading={processing}
        summary={`${item.userName} · ${formattedAmount} · ${
          item.workshopTitles?.[0] ?? ''
        }`}
      />

      {toast && (
        <div className='fixed bottom-6 left-0 right-0 px-4 z-[1000] flex justify-center'>
          <div className='w-full max-w-[360px]'>
            <Toast
              title={toast}
              hierarchy='important'
              direction='up'
              customWidth='100%'
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRefundsRow;
